export const fmColors = {
  brand: '#2c5530',
  brandDeep: '#1a3a1e',
  brandSoft: '#f7fcf5',
  success: '#52c41a',
  warning: '#faad14',
  info: '#1890ff',
  bgContainer: '#ffffff',
  surfaceMuted: '#f0f0f0',
  textSecondary: '#666666',
} as const;

export const fmRadii = {
  sm: 6,
  md: 8,
} as const;

export const fmSpacing = {
  sm: 8,
  md: 16,
} as const;

export type RouteTheme = 'dark' | 'light';

export const fmGlassTokens = {
  // DS canonical glass tokens
  glass: 'rgba(255, 255, 255, 0.72)', // DS glass light
  glass2: 'rgba(255, 255, 255, 0.86)', // DS glass-2 light
  glassDark: 'rgba(12, 24, 18, 0.58)', // DS glass dark
  glass2Dark: 'rgba(12, 24, 18, 0.78)', // DS glass-2 dark
  // Legacy names — backward compat (F5 removes)
  bg: 'rgba(20, 40, 22, 0.72)',
  bgLight: 'rgba(247, 252, 245, 0.85)',
  border: 'rgba(255, 255, 255, 0.12)',
  borderLight: 'rgba(44, 85, 48, 0.15)',
  shadow: '0 8px 30px rgba(20, 45, 32, 0.12)',
  shadowLight: '0 2px 12px rgba(44, 85, 48, 0.12)',
  blur: 'blur(18px) saturate(135%)',
  text: 'rgba(255, 255, 255, 0.90)',
  textMuted: 'rgba(255, 255, 255, 0.65)',
} as const;

/**
 * muem9.18 — Semantic section / media / control / map token NAME registry.
 *
 * The canonical definitions live in `src/styles/tokens.css` (single source of
 * truth for the values, incl. the central responsive `@media` overrides). This
 * object exposes the matching semantic NAMES as `var(--fm-*)` references so Fm
 * PRIMITIVES that must read a token from JS (inline style, recharts geometry,
 * portal-rendered frames) reference the role by name instead of hard-coding a
 * value. It is a name registry, NOT a magnitude mirror — no px/rem/hex literals.
 *
 * Consumed by Fm primitives ONLY. Route/page code must never import these
 * (page-purity forbids page consumption of implementation-only tokens).
 */
export const fmSemanticTokens = {
  section: {
    py: 'var(--fm-section-py)',
    bodyGap: 'var(--fm-section-body-gap)',
    eyebrowTitleGap: 'var(--fm-eyebrow-title-gap)',
    titleCopyGap: 'var(--fm-title-copy-gap)',
    copyStackGap: 'var(--fm-copy-stack-gap)',
    sectionGap: 'var(--fm-section-gap)',
  },
  band: {
    bg: 'var(--fm-band-bg)',
    fg: 'var(--fm-band-fg)',
    muted: 'var(--fm-band-muted)',
    border: 'var(--fm-band-border)',
  },
  rail: {
    shellMaxWide: 'var(--fm-shell-max-wide)',
    contentRailMax: 'var(--fm-content-rail-max)',
    proseMeasure: 'var(--fm-prose-measure)',
    splitGap: 'var(--fm-split-gap)',
  },
  figure: {
    surface: 'var(--fm-figure-surface)',
    surfaceFg: 'var(--fm-figure-surface-fg)',
    surfaceMuted: 'var(--fm-figure-surface-muted)',
    border: 'var(--fm-figure-border)',
    radius: 'var(--fm-figure-radius)',
    inset: 'var(--fm-figure-inset)',
  },
  selector: {
    trackBg: 'var(--fm-selector-track-bg)',
    trackBorder: 'var(--fm-selector-track-border)',
    trackInset: 'var(--fm-selector-track-inset)',
    gap: 'var(--fm-selector-gap)',
    itemPx: 'var(--fm-selector-item-px)',
    itemPy: 'var(--fm-selector-item-py)',
    radius: 'var(--fm-selector-radius)',
    activeBg: 'var(--fm-selector-active-bg)',
    activeFg: 'var(--fm-selector-active-fg)',
    radioDotSize: 'var(--fm-radio-dot-size)',
    radioDotInner: 'var(--fm-radio-dot-inner)',
  },
  table: {
    rowMinH: 'var(--fm-table-row-min-h)',
    cellPx: 'var(--fm-table-cell-px)',
    cellPy: 'var(--fm-table-cell-py)',
    hairline: 'var(--fm-table-hairline)',
    stickyBg: 'var(--fm-table-sticky-bg)',
    actionRailGap: 'var(--fm-action-rail-gap)',
    actionTargetSize: 'var(--fm-action-target-size)',
    actionOverflowTargetSize: 'var(--fm-action-overflow-target-size)',
    numericColumnMin: 'var(--fm-numeric-column-min)',
    moneyColumnMin: 'var(--fm-money-column-min)',
    deltaColumnMin: 'var(--fm-delta-column-min)',
    labelColumnMin: 'var(--fm-label-column-min)',
  },
  map: {
    frameBg: 'var(--fm-map-frame-bg)',
    frameBorder: 'var(--fm-map-frame-border)',
    overlaySurface: 'var(--fm-map-overlay-surface)',
    overlayFg: 'var(--fm-map-overlay-fg)',
    legendSwatchSize: 'var(--fm-map-legend-swatch-size)',
    legendGap: 'var(--fm-map-legend-gap)',
    calloutGap: 'var(--fm-map-callout-gap)',
    timelineTrack: 'var(--fm-map-timeline-track)',
    timelineActive: 'var(--fm-map-timeline-active)',
    timelineTick: 'var(--fm-map-timeline-tick)',
    timelineThumb: 'var(--fm-map-timeline-thumb)',
  },
  motion: {
    durationFast: 'var(--fm-motion-duration-fast)',
    durationData: 'var(--fm-motion-duration-data)',
    durationSection: 'var(--fm-motion-duration-section)',
    easeStandard: 'var(--fm-motion-ease-standard)',
    easeEmphasized: 'var(--fm-motion-ease-emphasized)',
    countupDuration: 'var(--fm-countup-duration)',
    countupStagger: 'var(--fm-countup-stagger)',
    revealDistance: 'var(--fm-reveal-distance)',
    revealOpacityStart: 'var(--fm-reveal-opacity-start)',
  },
} as const;

export const fmAccentTokens = {
  // DS dark accent (used in dark AntD config)
  accent: '#3fbf6a',
  // DS light accent
  accentLight: '#2f7d4a',
  accentStrong: '#2c5530',
  accentStrongDark: '#4fcf78',
  teal: '#5e897f',
  amber: '#a98a55',
  sky: '#66809a',
  rose: '#a4705f',
} as const;

/**
 * Route-prefix → declared theme. Longest prefix wins. Dark is reserved for the
 * map canvas root; every other app route reads on light content.
 */
export const ROUTE_THEME_MAP: Record<string, RouteTheme> = {
  '/app/operations-planning': 'light', // data view — light content (F2)
  '/app/watch': 'light', // data view — light content (F2)
  '/app/forest-overview': 'light',
  '/app/price-estimate': 'light',
  '/app/stand-detail': 'light',
  '/app/my-forests': 'light',
  '/app/browse-properties': 'light',
  '/app/measurements': 'light',
  '/app/timber-prices': 'light',
  '/app/settings': 'light',
  '/app/debug': 'light',
  '/app/about': 'light',
  '/app/implement': 'light', // implement workspace (formerly sell) — light content
  '/app/offer-decode': 'light', // received-offer guided flow, same light workspace as the tender
  '/app/satellite-history': 'light',
  '/app/climate-history': 'light',
  '/app/buyer-diligence': 'light',
  '/app': 'dark', // map root only — must be last (shortest)
};

/**
 * Resolve declared theme for a pathname.
 * Tries longest-prefix match first to avoid /app swallowing /app/forest-overview.
 */
export function resolveRouteTheme(pathname: string): RouteTheme {
  const keys = Object.keys(ROUTE_THEME_MAP).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (pathname === key || pathname.startsWith(`${key}/`)) {
      return ROUTE_THEME_MAP[key];
    }
  }
  return 'light';
}

/** Base token overrides shared by both form-control themes. colorPrimary = Lehti AI brand green. */
const sharedFormControlTokens = {
  colorPrimary: fmColors.brand, // Lehti AI brand green — MUST stay in both themes
  colorSuccess: fmColors.success,
  colorWarning: fmColors.warning,
  colorInfo: fmColors.info,
  borderRadius: fmRadii.md,
};

export const forestFormControlTheme = {
  token: {
    ...sharedFormControlTokens,
    // Transparent so the control follows the surface behind it (pale data band
    // OR white card) instead of painting a stark white box — the border defines
    // the field. Aligns AntD form controls with the DS "follow the background,
    // fill only for emphasis" surface rule. Dropdown popup stays a solid
    // elevated surface (colorBgElevated) since it floats above content.
    colorBgContainer: 'transparent',
    colorBgElevated: fmColors.brandSoft,
  },
};

export const forestDarkFormControlTheme = {
  token: {
    ...sharedFormControlTokens,
    colorBgContainer: '#1a2e1b',
    colorBgElevated: '#243626',
  },
};

/**
 * The brand tokens with NO surface or algorithm opinion, for the app-wide
 * provider that must not decide light vs dark.
 *
 * That provider must pass brand tokens: with no theme every AntD control it is
 * the nearest provider for — checkbox, radio, switch, slider, date picker —
 * renders in AntD's default #1677ff. FmSelect looks branded only because it
 * wraps its own themed provider. One saturated blue
 * checkbox inside an otherwise green surface is AntD theming the application,
 * which the design system forbids.
 *
 * Surfaces stay out on purpose: `colorBgContainer` is the token that decides
 * whether a field is transparent or a filled box, and the app-wide provider is
 * exactly the place that cannot know.
 */
export const forestInheritFormControlTheme = {
  token: { ...sharedFormControlTokens },
};
